import { FC, useState } from 'react';
import { CheckCircleFilled } from '@ant-design/icons';
import { Button, Progress, Space, Typography } from 'antd';
import styled from 'styled-components';
import { useBillingPlansContext } from '@/entities/billing-plan';
import { PlanResponse, PlanUsageResponse } from '@/shared/api';
import { useThemeContext, themeConfig } from '@/shared/theme';
import { Col, Row } from '@/shared/ui';
import { FreePlanModal } from './FreePlanModal';

type Props = { isActive?: boolean } & PlanResponse & PlanUsageResponse;

const { Title } = Typography;

export const FreePlan: FC<Props> = ({ isActive, ...props }) => {
  const { theme } = useThemeContext();
  const { activePlan } = useBillingPlansContext();

  const [modalOpen, setModalOpen] = useState(false);

  if (isActive) {
    return (
      <PlanWrapper
        style={{
          border: `1px solid ${themeConfig[theme].colors.primary}`,
        }}
      >
        <div style={{ position: 'absolute', right: 10, top: 10 }}>
          <CheckCircleFilled
            style={{ fontSize: 18, color: themeConfig[theme].colors.primary }}
          />
        </div>
        <PlanTitle level={2}>{props.title}</PlanTitle>
        <Description>{props.description}</Description>
        <Space size="middle" direction="vertical" style={{ width: '100%' }}>
          <div>
            <label>Users</label>
            <Progress
              showInfo={false}
              gapPosition="bottom"
              strokeColor={
                props.usage?.users.current >= props.usage?.users.limit
                  ? themeConfig[theme].colors.error
                  : themeConfig[theme].colors.primary
              }
              percent={
                (props.usage?.users.current! / props.usage?.users.limit!) * 100
              }
            />
            <Quota justifyContent="space-between">
              <div>
                {props.usage?.users.current} (
                {Math.round(
                  (props.usage?.users.current! / props.usage?.users.limit!) *
                    100,
                )}
                %)
              </div>
              <div>{props.usage?.users.limit}</div>
            </Quota>
          </div>
          <div>
            <label>Workspaces</label>
            <Progress
              showInfo={false}
              gapPosition="bottom"
              strokeColor={
                props.usage?.workspaces.current >= props.usage?.workspaces.limit
                  ? themeConfig[theme].colors.error
                  : themeConfig[theme].colors.primary
              }
              percent={
                (props.usage?.workspaces.current! /
                  props.usage?.workspaces.limit!) *
                100
              }
            />
            <Quota justifyContent="space-between">
              <div>
                {props.usage?.workspaces.current} (
                {Math.round(
                  (props.usage?.workspaces.current! /
                    props.usage?.workspaces.limit!) *
                    100,
                )}
                %)
              </div>
              <div>{props.usage?.workspaces.limit}</div>
            </Quota>
          </div>
          <div>
            <label>Published procs</label>
            <Progress
              showInfo={false}
              strokeColor={
                props.usage?.publishedProcs.current >=
                props.usage?.publishedProcs.limit
                  ? themeConfig[theme].colors.error
                  : themeConfig[theme].colors.primary
              }
              percent={
                (props.usage?.publishedProcs.current! /
                  props.usage?.publishedProcs.limit!) *
                100
              }
            />
            <Quota justifyContent="space-between">
              <div>
                {props.usage?.publishedProcs.current} (
                {Math.round(
                  (props.usage?.publishedProcs.current! /
                    props.usage?.publishedProcs.limit!) *
                    100,
                )}
                %)
              </div>
              <div>{props.usage?.publishedProcs.limit}</div>
            </Quota>
          </div>
          <div>
            <label>Running procs</label>
            <Progress
              showInfo={false}
              gapPosition="bottom"
              strokeColor={
                props.usage?.instances.current >= props.usage?.instances.limit
                  ? themeConfig[theme].colors.error
                  : themeConfig[theme].colors.primary
              }
              percent={
                (props.usage?.instances.current! /
                  props.usage?.instances.limit!) *
                100
              }
            />
            <Quota justifyContent="space-between">
              <div>
                {props.usage?.instances.current} (
                {Math.round(
                  (props.usage?.instances.current! /
                    props.usage?.instances.limit!) *
                    100,
                )}
                %)
              </div>
              <div>{props.usage?.instances.limit}</div>
            </Quota>
          </div>
        </Space>
      </PlanWrapper>
    );
  }

  return (
    <>
      <PlanWrapper
        style={{
          border: `1px solid ${themeConfig[theme].colors.borderHover}`,
        }}
      >
        <PlanTitle level={2}>{props.title}</PlanTitle>
        <Description>{props.description}</Description>
        <Col justifyContent="space-between" style={{ flex: 1, width: '100%' }}>
          <Space direction="vertical" style={{ marginTop: 20, width: '100%' }}>
            <Row justifyContent="space-between" style={{ width: '100%' }}>
              <div>Users</div>
              <div>{props.features.users}</div>
            </Row>
            <Row justifyContent="space-between" style={{ width: '100%' }}>
              <div>Workspaces</div>
              <div>{props.features.workspaces}</div>
            </Row>
            <Row justifyContent="space-between" style={{ width: '100%' }}>
              <div>Published procs</div>
              <div>{props.features.publishedProcsCount}</div>
            </Row>
            <Row justifyContent="space-between" style={{ width: '100%' }}>
              <div>Running procs</div>
              <div>{props.features.instancesCount}</div>
            </Row>
            {!activePlan?.subscriptionCancelDate && (
              <Button
                type="primary"
                size="large"
                block
                onClick={() => setModalOpen(true)}
                style={{ marginTop: 50 }}
              >
                Downgrade to Free plan
              </Button>
            )}
          </Space>
          {activePlan?.subscriptionCancelDate && (
            <div style={{ fontSize: 12, marginTop: 30, textAlign: 'center' }}>
              From{' '}
              <strong>
                {new Date(activePlan.subscriptionCancelDate)
                  .toDateString()
                  .substring(4)}
                .
              </strong>
              <br />
              you will be on Free plan.
            </div>
          )}
        </Col>
      </PlanWrapper>
      <FreePlanModal
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onSuccess={() => setModalOpen(false)}
      />
    </>
  );
};

const PlanWrapper = styled(Col)`
  padding: 2rem;
  border-radius: 8px;
  width: 300px;
  max-width: 100%;
  min-height: 435px;
  min-width: 225px;
  position: relative;
  flex: 1;
`;

const PlanTitle = styled(Title)`
  margin-bottom: 0 !important;
`;

const Description = styled.div`
  font-size: 13px;
  color: #8c8c8c;
  margin-bottom: 20px;
`;

const Quota = styled(Row)`
  font-size: 12px;
  margin-top: -5px;
  opacity: 0.7;
`;
